import { useGlobalSearchParams, useRouter } from "expo-router";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { SafeAreaView } from "react-native-safe-area-context";

import PlayerDetailCenterView from "./PlayerDetailCenterView";
import { getRosterValues, labelToMetricId } from "@/utils/playerDetailCenter";

import SkeletonPlayerDetail from "@/components/organisms/SkeletonPlayerDetail";
import { Box } from "@/components/atoms/Box";
import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";
// design-system: ui-util waiver — useBreakpointValue has no atom-layer home yet (foundation follow-up)
// eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: unavoidable primitive usage outside atoms (@/components/ui import(s): @/components/ui/utils/use-break-point-value) —…
import { useBreakpointValue } from "@/components/ui/utils/use-break-point-value";
import { useCurrentUser } from "@/features/auth";
import { useClientFeatureFlags } from "@/hooks/useClientFeatureFlags";
import {
  CurrencyCode,
  PlayerDetailPlayerBioDataFragment,
  PlayerDetailPlayerMetaFragment,
  usePlayerDetailListPlayersByTeamIdsAndPositionIdsQuery,
  Watchlist,
} from "@/generated/graphql";
import useMobileScreen from "@/hooks/useMobileScreen";
import { usePlayerDetailQuery } from "@/hooks/usePlayerDetailQuery";
import { useTheme } from "@/providers/ThemeProvider";
import { ICompositeMetric } from "@/stores/compositeMetric";
import chunkArrayWithPadding from "@/utils/chunkWithPadding";
import { getPlayerCompositeMetrics } from "@/utils/getPlayerCompositeMetrics";

/**
 * Props for PlayerDetailCenterContainer component
 */
interface ICenterColumnProps {
  playerId?: string;
  /**
   * Player already fetched by a parent (the player-detail page's MainContainer
   * owns `PlayerDetailGetPlayer`). When provided, this component reuses it and
   * SKIPS its own `usePlayerDetailQuery`, so the page issues a single
   * `PlayerDetailGetPlayer` network request instead of two competing
   * consumers each fetching on their own gate (SE-5038). Standalone usages
   * (modals, kanban sidebar) omit it and self-fetch as before.
   */
  player?: PlayerDetailPlayerMetaFragment | null;
  /** Loading state for an externally-provided {@link player}. */
  playerLoading?: boolean;
  /**
   * When true, this component does NOT fetch `PlayerDetailGetPlayer`. Used by
   * the list-screen master-detail pane (roster-management / watchlist) to skip
   * its query while the list screen is NOT the focused route — i.e. when a
   * dedicated `/players/{id}` detail route is pushed on top and its own
   * PlayerDetailCenter is the canonical owner. Skip-when-unfocused only, never
   * skip-always: the focused list screen's inline pane still fetches once
   * (SE-5038 AC2).
   */
  pauseQuery?: boolean;
  activeWatchList?: Watchlist;
  onAddPlayer?: () => void;
  isGoBack?: boolean;
  isSimilarDetail?: boolean;
}

/**
 * Empty roster-comparison array used before any metric has been picked.
 * @remarks
 * Module-level purely so the "nothing selected yet" branch has one constant
 * identity. It never reaches a consumer: it is produced only while
 * `selectedMetricLabel` is null, which is exactly when `selectedMetricId` is
 * also null, and `MetricExplanationModalContainer` returns null before
 * rendering anything in that case. Treat it as read-only regardless — every
 * downstream consumer of `rosterValues` is non-mutating today.
 */
const NO_ROSTER_VALUES: number[] = [];

/**
 * Resolves the raw numeric metric value for a given label from player bioData.
 * @param label - The metric label to resolve (e.g., "Player Style Fit", "Gemini Player Rating")
 * @param bioData - The player's biographical data fragment containing metric values
 * @returns The numeric metric value, or null if unavailable or bioData is missing
 * @remarks
 * - Player Style Fit is converted to a percentage by multiplying fitScore by 100
 * - Returns null for missing data or unrecognized labels
 */
const getRawMetricValue = (
  label: string,
  bioData: PlayerDetailPlayerBioDataFragment | null | undefined
): number | null => {
  if (!bioData) return null;

  const metricMap: Record<string, number | null | undefined> = {
    "Player Style Fit": bioData.fitScore ? bioData.fitScore * 100 : null,
    "Team Style Fit": bioData.teamStyleFit,
    "Gemini Player Rating": bioData.gpr,
    Carrying: bioData.carryingTransGlobalCategoricalScore,
    Dribbling: bioData.dribblingTransGlobalCategoricalScore,
    "GK Passing": bioData.gkPassingTransCategoricalScore,
    Passing: bioData.passingTransGlobalCategoricalScore,
    Shooting: bioData.shotLocationTransGlobalCategoricalScore,
    Positioning: bioData.gkPositioningTransCategoricalScore,
    "Shot Stopping": bioData.gkShotStoppingTransCategoricalScore,
    "Physical Score": bioData.physicalScore,
  };

  return metricMap[label] ?? null;
};

/**
 * Renders empty state when no player is selected on larger screens
 * @param props - Component props
 * @param props.isSmallScreen - Whether the current screen is small
 * @param props.isSimilarDetail - Whether rendering in similar players comparison view
 */
const EmptyPlayerState = ({
  isSmallScreen,
  isSimilarDetail,
}: {
  isSmallScreen: boolean;
  isSimilarDetail?: boolean;
}) => (
  <Stack className="flex-1 px-3">
    <Box
      className={`${!isSmallScreen ? "flex-1" : ""} flex-row items-center justify-center ${isSimilarDetail ? "pt-3" : undefined}`}
    >
      <Heading variant={!isSmallScreen ? "title" : "title-lg"}>
        Select a player to view details
      </Heading>
    </Box>
  </Stack>
);

/**
 * Container component for player detail center column with composite metrics and modal management
 * @param root0 - Component props
 * @param root0.playerId - Unique identifier of the player to display details for
 * @param root0.activeWatchList - Currently selected watchlist for player context
 * @param root0.onAddPlayer - Callback invoked when adding player to watchlist
 * @param root0.isGoBack - Whether to show back navigation button
 * @param root0.isSimilarDetail - Whether rendering in similar players comparison view
 * @param root0.player - Player pre-fetched by a parent; when set, skips this component's own query (SE-5038)
 * @param root0.playerLoading - Loading state for an externally-provided player
 * @param root0.pauseQuery - When true, skip the own PlayerDetailGetPlayer fetch (unfocused list-screen pane, SE-5038 AC2)
 */
const PlayerDetailCenterContainer = ({
  playerId: passedPlayerId,
  player: providedPlayer,
  playerLoading: providedLoading,
  pauseQuery = false,
  ...props
}: ICenterColumnProps) => {
  const numCols = useBreakpointValue({ default: 2, lg: 3 });
  const router = useRouter();
  const currentUser = useCurrentUser();
  const { playerPageHeaderEnabled } = useClientFeatureFlags();
  const [flagToRenderModalDetail, setFlagToRenderModalDetail] =
    useState<boolean>(true);
  const isMobile = useMobileScreen();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  // New player page layout (with tabs) is used on both desktop and mobile
  const useNewPlayerPageLayout = playerPageHeaderEnabled;
  const isSmallScreen = isMobile;
  const searchParams = useGlobalSearchParams();
  const playerId = passedPlayerId ?? searchParams.playerId;

  // When a parent OWNS the player query (the player-detail page's
  // MainContainer owns PlayerDetailGetPlayer), this component reuses the
  // parent's `player` and SKIPS its own query, so the page issues a single
  // PlayerDetailGetPlayer round-trip instead of two competing consumers each
  // fetching on their own gate (SE-5038). Ownership is signalled by the
  // parent passing `playerLoading` (always a boolean when it manages the
  // query) — not by `player` presence, which is `undefined` during the
  // parent's own loading window and must NOT trigger a fallback self-fetch.
  // Standalone usages (modals, kanban sidebar) omit `playerLoading` and
  // self-fetch as before.
  const isPlayerManagedByParent = providedLoading !== undefined;
  const { data, loading: queryLoading } = usePlayerDetailQuery({
    variables: { id: Array.isArray(playerId) ? playerId[0] : (playerId ?? "") },
    skip: !playerId || isPlayerManagedByParent || pauseQuery,
  });

  const player = useMemo(
    () => (isPlayerManagedByParent ? providedPlayer : data?.getPlayer),
    [isPlayerManagedByParent, providedPlayer, data?.getPlayer]
  );
  const loading = isPlayerManagedByParent
    ? Boolean(providedLoading)
    : queryLoading;

  const teamIds = useMemo(
    () =>
      (currentUser?.organization?.organizationTeams ?? []).map(l => l.teamId),
    [currentUser?.organization?.organizationTeams]
  );

  const positionIds = useMemo(
    () =>
      (player?.positions ?? []).map(p => p?.id).filter((p): p is string => !!p),
    [player?.positions]
  );

  const {
    data: listPlayerSameTeamAndPosition,
    loading: isFetchingListPlayerSameTeamAndPosition,
  } = usePlayerDetailListPlayersByTeamIdsAndPositionIdsQuery({
    variables: { teamIds, positionIds },
    skip: teamIds.length === 0 || positionIds.length === 0,
  });

  const currencyCode =
    currentUser?.organization?.currencyCode ?? CurrencyCode.Eur;

  const bioData = useMemo(() => player?.bioData, [player?.bioData]);

  const scoutReports = useMemo(
    () => player?.scoutReports ?? [],
    [player?.scoutReports]
  );

  const playerCompositeMetrics = useMemo(
    () =>
      getPlayerCompositeMetrics(
        bioData
          ? {
              ...bioData,
              contractExpires: bioData.contractExpires
                ? new Date(bioData.contractExpires).toISOString()
                : null,
            }
          : bioData,
        currencyCode,
        true,
        false,
        isDark,
        scoutReports,
        true
      ),
    [bioData, currencyCode, isDark, scoutReports]
  );

  const playerCompositeMetricsArray = chunkArrayWithPadding<ICompositeMetric>(
    playerCompositeMetrics,
    numCols
  );

  const [explanationModalOpen, setExplanationModalOpen] = useState(false);
  /*
   * SE-7447: hold only the user's SELECTION (which metric was tapped) in state
   * and derive everything downstream of a query result from live data.
   *
   * The previous shape called `getRosterValues` inside `handleMetricSelect` and
   * stored the result in `useState`. `getRosterValues` returns `[]` while
   * `PlayerDetailListPlayersByTeamIdsAndPositionIds` is still in flight, so a
   * modal opened during that window kept an empty roster array for its entire
   * lifetime: the peer values landed in the Apollo cache moments later and the
   * comparison DotPlot never appeared, no matter how long the modal stayed
   * open. Only closing and reopening took a fresh snapshot.
   *
   * Deriving instead means the loading -> resolved transition of the very same
   * query this component already subscribes to (above) re-renders the modal
   * with real values, and SE-7394's `rosterDataState` machine — which promotes
   * `ready` over `loading` — turns the spinner into the chart with no further
   * change. This mirrors the already-correct pattern in
   * `src/features/player-metric-breakdown/screens/Main/MainContainer.tsx`.
   *
   * The trade is not free, and it runs both ways: an open modal now FOLLOWS
   * live data downward too. If `bioData` ever went missing while `player`
   * stayed truthy — the partial-normalized-cache shape PROJECT_RULES warns
   * about in the SE-5058 Apollo notes — `selectedMetricValue` would go null
   * and the chart region would collapse to `hidden` under the reader. That is
   * the better trade (it needs a cache shape nobody has observed here, whereas
   * the frozen snapshot's failure mode is the reported bug), but it is a real
   * edge, not a free lunch.
   */
  const [selectedMetricLabel, setSelectedMetricLabel] = useState<string | null>(
    null
  );
  const [transferValueModalOpen, setTransferValueModalOpen] = useState(false);

  const metricPositions = useMemo(
    () => player?.positions?.map(p => ({ name: p?.name })) ?? null,
    [player?.positions]
  );

  const selectedMetricId = useMemo(
    () =>
      selectedMetricLabel === null
        ? null
        : labelToMetricId(selectedMetricLabel, metricPositions),
    [selectedMetricLabel, metricPositions]
  );

  const selectedMetricValue = useMemo<number | string | null>(
    () =>
      selectedMetricLabel === null
        ? null
        : getRawMetricValue(selectedMetricLabel, bioData),
    [selectedMetricLabel, bioData]
  );

  const selectedRosterValues = useMemo(
    () =>
      selectedMetricLabel === null
        ? NO_ROSTER_VALUES
        : getRosterValues(
            selectedMetricLabel,
            listPlayerSameTeamAndPosition,
            metricPositions,
            player?.id
          ),
    [
      selectedMetricLabel,
      listPlayerSameTeamAndPosition,
      metricPositions,
      player?.id,
    ]
  );

  const handleMetricSelect = useCallback(
    (compositeMetric: ICompositeMetric) => {
      setSelectedMetricLabel(compositeMetric.label);
      setExplanationModalOpen(true);
    },
    []
  );

  const handleCloseModal = useCallback(() => {
    setExplanationModalOpen(false);
  }, []);

  const handleTransferValueRangeClick = useCallback(() => {
    setTransferValueModalOpen(true);
  }, []);

  const handleCloseTransferValueModal = useCallback(() => {
    setTransferValueModalOpen(false);
  }, []);

  const handleNavigateToReports = useCallback(() => {
    router.push(`./${playerId}/reports`);
  }, [playerId, router]);

  useEffect(() => {
    const timer = setTimeout(() => setFlagToRenderModalDetail(false), 300);
    return () => clearTimeout(timer);
  }, []);

  if (!playerId && !isSmallScreen) {
    return (
      <SafeAreaView edges={["top"]} className="flex-1">
        <EmptyPlayerState
          isSmallScreen={isSmallScreen}
          isSimilarDetail={props.isSimilarDetail}
        />
      </SafeAreaView>
    );
  }

  if (!player) {
    return (
      <SafeAreaView edges={isSmallScreen ? ["top"] : []} className="flex-1">
        <Stack className="flex-1 px-3">
          <SkeletonPlayerDetail />
        </Stack>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView edges={isSmallScreen ? ["top"] : []} className="flex-1">
      <PlayerDetailCenterView
        playerCompositeMetricsArray={playerCompositeMetricsArray}
        player={player}
        listPlayerSameTeamAndPosition={
          flagToRenderModalDetail ? undefined : listPlayerSameTeamAndPosition
        }
        isFetchingListPlayerSameTeamAndPosition={
          flagToRenderModalDetail ||
          isFetchingListPlayerSameTeamAndPosition ||
          loading
        }
        numCols={numCols}
        isSmallScreen={isSmallScreen}
        isDark={isDark}
        explanationModalOpen={explanationModalOpen}
        selectedMetricId={selectedMetricId}
        selectedMetricValue={selectedMetricValue}
        selectedRosterValues={selectedRosterValues}
        handleMetricSelect={handleMetricSelect}
        handleCloseModal={handleCloseModal}
        onNavigateToReports={handleNavigateToReports}
        transferValueModalOpen={transferValueModalOpen}
        handleTransferValueRangeClick={handleTransferValueRangeClick}
        handleCloseTransferValueModal={handleCloseTransferValueModal}
        currencyCode={currencyCode}
        playerPageHeaderEnabled={useNewPlayerPageLayout}
        {...props}
      />
    </SafeAreaView>
  );
};

export default PlayerDetailCenterContainer;
